import { isWhitelabel } from '@digital-retail/store-manager';
import { cx } from 'cvb';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

import { AdditionalPDPLabel } from '../../../../components/AdditionalPDPLabel/AdditionalPDPLabel';
import Availability from '../../../../components/Availability/Availability';
import { AvailabilityLocation } from '../../../../components/Availability/AvailabilityLocation/AvailabilityLocation';
import { getZoneName } from '../../../../components/Availability/DeliveryOptions/DeliveryOptions.Helper';
import { Badges } from '../../../../components/Badges/Badges';
import CMRApertura from '../../../../components/CMRApertura/CMRApertura';
import CMRCalculatorDesktop from '../../../../components/CMRCalculator/Desktop';
import Fomo from '../../../../components/Fomo/Fomo';
import InternationalShipping from '../../../../components/InternationalShipping/InternationalShipping';
import MultipurposeBadge from '../../../../components/MultipurposeBadge/MultipurposeBadge';
import ProductColorSwatches from '../../../../components/ProductColorSwatches/ProductColorSwatches';
import ProductPrices from '../../../../components/ProductPrices/ProductPrices';
import { ReaconditionedContainer } from '../../../../components/ReaconditionedContainer/ReaconditionedContainer';
import SellerInfo from '../../../../components/SellerInfo/SellerInfo';
import Size from '../../../../components/Size/Size';
import StockQuantity from '../../../../components/StockQuantity/StockQuantity';
import BodyCopy from '../../../../components/ui/BodyCopy/BodyCopy';
import Button from '../../../../components/ui/Button/Button';
import StockTable from '../../../../components/ui/StockTable/StockTable';
import constants from '../../../../config/constants';
import { withApplicationContext } from '../../../../utils/ApplicationContext';
import { withLabels } from '../../../../utils/LabelsContext';
import _ from '../../../../utils/LodashImports';
import { noop } from '../../../../utils/noop';
import {
  getPriceForOutOfStock,
  getSizeSelectionLabel,
  isReaconditionedProduct,
} from '../../../../utils/product/productUtils';
import { withProduct } from '../../../../utils/ProductContext';
import { getVariant } from '../../../../utils/variant';
import ProductQuantitySelectionContainer from '../../Hardline/Desktop/ProductQuantitySelectionContainer';
import { ProductSpecificationsDesktopStyle } from './ProductSpecificationsDesktop.style';

const ProductSpecificationsDesktop = ({
  product,
  labels,
  appCtx,
  isPumAvailable,
  pumQuantity,
  primeShipping,
  colorVariants,
  selectedColorSwatchIndex,
  colorSwatchHandler,
  sizes,
  selectedSize,
  sizeChangeHandler,
  handleAddToCart,
  isAddToCartInProgress,
  handleRemoveFromCart,
  isRemoveFromCartInProgress,
  addToCartButtonVisible,
  showCollectionButtonVisible,
  isVendorLogin,
  storeSkuData,
  currentTab,
  errorAddingToCart,
  requestProductFromStore,
  setShowLoginForm,
  showLoginForm,
  setErrorAddingToCart,
  sizeChartData,
  fomo,
  variantSwatchType,
}) => {
  const { regionCode, store, siteConfig, comuna: comunaData, isDefaultComuna } = appCtx || {};
  const { MAX_CART_PRODUCT, FIRST_PARTY_SELLER_REGEX, DEVICE_DESKTOP, PRODUCT_CONSTANTS } = constants;
  const { CMR_INSTALLMENT_ERROR_MESSAGE, COLOR, SEE_COLLECTION_BUTTON_LABEL } = labels;
  const { textDictionary, toggles } = siteConfig;

  const {
    id: productId,
    currentVariant: currentVariantId,
    variants,
    additionalPDPLabels,
    isOutOfStock,
    colorMetrics,
    cmrBannerActive = true,
    darSectionActive = false,
    attributes,
  } = product;

  const {
    badges = [],
    meatStickers = [],
    promotions,
    prices,
    cmrPoints,
    discountBadge,
    cmrSavings,
    maxOnCartAmount: maxCartQuantity = MAX_CART_PRODUCT,
    multipurposeBadges = [],
  } = getVariant(variants, currentVariantId);

  const { pumUnits } = attributes;

  const fomoMessage = _.get(textDictionary, 'FOMO_MESSAGE', 'Últimas unidades');
  const allowed3PCampaignNamesToRender = _.get(
    textDictionary,
    'ALLOWED_3P_CAMPAIGN_NAMES_TO_RENDER',
    'ENVIO GRATIS 3P, ENVIO_GRATIS_3P'
  );

  const {
    isCMRAperturaEnabled = false,
    isMultipurposeBadgeApplicable = false,
    isMeatStickerApplicableInPDP = false,
    isInternationalBuyVisiblePDP = false,
    isStoreAvailabilityVisibleInPDP = false,
    isCMRCalculatorEnabledPDP = false,
  } = toggles;

  const freeShippingPromotionFor3p = promotions?.find((promotion) =>
    allowed3PCampaignNamesToRender
      .split(',')
      .map((name) => name.trim())
      .includes(promotion.campaignName)
  );
  const hasStickers = (isMeatStickerApplicableInPDP && meatStickers.length > 0) || freeShippingPromotionFor3p;
  const nonEventMeatStickers = (meatStickers || []).filter((sticker) => !['event'].includes(sticker.type));
  const sellerName = _.get(variants, '[0].offerings[0].sellerName', '');
  const is1pSeller = FIRST_PARTY_SELLER_REGEX.test(sellerName.toLowerCase());
  const hasNoVariants = sizes.length === 0;
  const fomoEnabled = fomo?.enabled ?? false;

  const isStoreDataVaild = storeSkuData && Object.keys(storeSkuData).length > 0;

  const addToCartLabel = _.get(additionalPDPLabels, 'addToCart.enabled', false)
    ? _.get(additionalPDPLabels, 'addToCart', {})
    : null;

  const isReaconditionedProd = isReaconditionedProduct(product);
  const isWhitelabelSite = isWhitelabel({ store });

  const selectedZone = comunaData?.data || null;
  const defaultZoneConfig =
    selectedZone && !_.isEmpty(selectedZone)
      ? selectedZone
      : {
          region: {},
          comuna: {},
        };

  const [zoneConfig, setZoneConfig] = useState(defaultZoneConfig);

  const { comunaName } = getZoneName(selectedZone, isDefaultComuna) || { comunaName: '' };

  useEffect(() => {
    if (selectedZone && !isDefaultComuna) setZoneConfig({ ...selectedZone });
  }, [selectedZone]);

  if (isOutOfStock) {
    return (
      <div
        className="product-specifications"
        data-backend-category={product.merchantCategoryId}
        data-availability={product.attributes && product.attributes.isPrimeAvailable}
        data-published={product.isPublished}
        data-variations={variants.length}
      >
        <div className="product-specifications-column fa--product-specifications-column__desktop">
          {prices && prices.length > 0 && (
            <div className="prices-container">
              <div className="prices">
                <ProductPrices
                  isVendorLogin={isVendorLogin}
                  currentTab={currentTab}
                  storeSkuData={storeSkuData}
                  prices={getPriceForOutOfStock(prices)}
                  productId={productId}
                  regionCode={regionCode}
                  cmrPoints={cmrPoints}
                  pumUnit={pumUnits}
                  isPumAvailable={isPumAvailable}
                  pumQuantity={pumQuantity}
                  primeShipping={primeShipping}
                  deviceType={DEVICE_DESKTOP}
                />
                {hasNoVariants && fomoEnabled && <Fomo text={fomoMessage} />}
              </div>
            </div>
          )}
          <SellerInfo product={product} />
          {isReaconditionedProd && <ReaconditionedContainer appCtx={appCtx} productAttributes={product} />}
        </div>
        <style jsx>{ProductSpecificationsDesktopStyle}</style>
      </div>
    );
  }
  const sizeSelectionLabel = getSizeSelectionLabel(product, labels.SELECTION_SIZE);

  return (
    <div
      className="product-specifications"
      data-backend-category={product.merchantCategoryId}
      data-availability={product.attributes && product.attributes.isPrimeAvailable}
      data-published={product.isPublished}
      data-variations={variants.length}
    >
      <div className="product-specifications-column fa--product-specifications-column__desktop">
        <SellerInfo product={product} />

        {prices && prices.length > 0 && (
          <div
            className={`prices-container
          ${isMultipurposeBadgeApplicable && multipurposeBadges.length > 0 ? 'withMultipurposeBadge' : ''}
          `}
          >
            <div className="prices">
              <ProductPrices
                isVendorLogin={isVendorLogin}
                currentTab={currentTab}
                storeSkuData={storeSkuData}
                prices={prices}
                productId={productId}
                regionCode={regionCode}
                cmrPoints={cmrPoints}
                pumUnit={pumUnits}
                isPumAvailable={isPumAvailable}
                pumQuantity={pumQuantity}
                primeShipping={primeShipping}
                deviceType={DEVICE_DESKTOP}
                discountBadge={discountBadge}
                darSectionActive={darSectionActive}
              />
            </div>
            {isCMRCalculatorEnabledPDP && (
              <div className="cmrCalculator-container">
                <CMRCalculatorDesktop errorMessage={CMR_INSTALLMENT_ERROR_MESSAGE} layout="softline" />
              </div>
            )}
          </div>
        )}
        {isReaconditionedProd && <ReaconditionedContainer appCtx={appCtx} productAttributes={product} />}
        {isInternationalBuyVisiblePDP && <InternationalShipping product={product} />}
        {(badges?.length || meatStickers?.length || freeShippingPromotionFor3p?.badge) && (
          <div className="meatstickers-container">
            <Badges
              badgeItems={isMeatStickerApplicableInPDP ? nonEventMeatStickers : badges}
              layout="PDP"
              isSticker={hasStickers}
              promotionBadge={freeShippingPromotionFor3p?.badge}
            />
          </div>
        )}
        {isMultipurposeBadgeApplicable && multipurposeBadges.length > 0 && (
          <div className="multipurpose-badge">
            <MultipurposeBadge badgeItems={multipurposeBadges} deviceType="desktop" inPdp />
          </div>
        )}

        <StockQuantity />

        {colorVariants[selectedColorSwatchIndex] ? (
          <div className="colorSwatches-container">
            <BodyCopy isHighlighted size="copy3">{`${COLOR}: `}</BodyCopy>
            <BodyCopy size="copy3">{colorVariants[selectedColorSwatchIndex].label}</BodyCopy>
            <ProductColorSwatches
              colorVariants={colorVariants}
              productId={product.id}
              selectedColorSwatchIndex={selectedColorSwatchIndex}
              colorSwatchHandler={colorSwatchHandler}
              type={PRODUCT_CONSTANTS.SOFTLINE}
              size="medium"
              appCtx={appCtx}
              colorMetrics={colorMetrics}
              variantSwatchType={variantSwatchType}
            />
          </div>
        ) : (
          <div className="specifications-divider" />
        )}
        {sizes.length !== 0 && (
          <div className="size-container mkp-softline-size">
            <Size
              title={sizeSelectionLabel}
              sizes={sizes}
              onSizeChange={sizeChangeHandler}
              selectedSize={selectedSize}
              isSoftline
              sizeChartData={sizeChartData}
              fomo={fomo}
            />
          </div>
        )}
        {isVendorLogin && isStoreDataVaild && (
          <StockTable
            stockData={storeSkuData}
            colorVariants={colorVariants}
            selectedColorSwatchIndex={selectedColorSwatchIndex}
            sizes={sizes}
            requestProductFromStore={requestProductFromStore}
            regionCode={regionCode}
          />
        )}

        {addToCartLabel && (
          <AdditionalPDPLabel label={addToCartLabel} type="addToCart" productType="softline" desktop />
        )}
        <ProductQuantitySelectionContainer
          errorAddingToCart={errorAddingToCart}
          addToCartButtonVisible={addToCartButtonVisible}
          isAddToCartInProgress={isAddToCartInProgress}
          labels={labels}
          maxCartQuantity={maxCartQuantity}
          handleAddToCart={handleAddToCart}
          handleRemoveFromCart={handleRemoveFromCart}
          isRemoveFromCartInProgress={isRemoveFromCartInProgress}
          isVendorLogin={isVendorLogin}
          sizes={sizes}
          selectedSize={selectedSize}
          setShowLoginForm={setShowLoginForm}
          showLoginForm={showLoginForm}
          setErrorAddingToCart={setErrorAddingToCart}
          layout="softline"
        />
        {!!product.showShippingModal && (
          <div className={cx('delivery-options-wrapper', isWhitelabelSite && 'whitelabel')}>
            {comunaName && (
              <div className="avl-content">
                <AvailabilityLocation comunaName={comunaName} />
              </div>
            )}
            <div className="dlv-opt-wrapper">
              <Availability
                isSoftline
                sizes={sizes}
                colorVariants={colorVariants}
                regionCode={regionCode}
                store={store}
                isStoreAvailabilityVisibleInPDP={isStoreAvailabilityVisibleInPDP}
                is1pSeller={is1pSeller}
                selectedZone={selectedZone}
                zoneConfig={zoneConfig}
                setZoneConfig={setZoneConfig}
                isDefaultComuna={isDefaultComuna}
              />
            </div>
          </div>
        )}
        {showCollectionButtonVisible && (
          <div className="addToCart-container fa--add-to-cart__desktop">
            <Button type="outline" onClick={handleAddToCart} size="xtra-large">
              <span>
                {SEE_COLLECTION_BUTTON_LABEL}
                <i className="csicon-arrow_down_small" />
              </span>
            </Button>
          </div>
        )}
        {isCMRAperturaEnabled && cmrBannerActive && <CMRApertura prices={prices} cmrSavings={cmrSavings} isSoftline />}
      </div>
      <style jsx>{ProductSpecificationsDesktopStyle}</style>
    </div>
  );
};

ProductSpecificationsDesktop.defaultProps = {
  isPumAvailable: false,
  pumQuantity: 0,
  primeShipping: false,
  colorVariants: [],
  selectedColorSwatchIndex: 0,
  colorSwatchHandler: noop,
  sizes: [],
  selectedSize: '',
  sizeChangeHandler: noop,
  handleAddToCart: noop,
  requestProductFromStore: noop,
  isAddToCartInProgress: false,
  handleRemoveFromCart: noop,
  isRemoveFromCartInProgress: false,
  addToCartButtonVisible: true,
  showCollectionButtonVisible: false,
  storeSkuData: [],
  currentTab: undefined,
  errorAddingToCart: false,
  showLoginForm: false,
  sizeChartData: {},
  fomo: {},
  variantSwatchType: undefined,
};

ProductSpecificationsDesktop.propTypes = {
  product: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
  appCtx: PropTypes.object.isRequired,
  isPumAvailable: PropTypes.bool,
  pumQuantity: PropTypes.number,
  primeShipping: PropTypes.bool,
  colorVariants: PropTypes.array,
  selectedColorSwatchIndex: PropTypes.number,
  colorSwatchHandler: PropTypes.func,
  sizes: PropTypes.array,
  selectedSize: PropTypes.string,
  sizeChangeHandler: PropTypes.func,
  handleAddToCart: PropTypes.func,
  requestProductFromStore: PropTypes.func,
  isAddToCartInProgress: PropTypes.bool,
  handleRemoveFromCart: PropTypes.func,
  isRemoveFromCartInProgress: PropTypes.bool,
  addToCartButtonVisible: PropTypes.bool,
  showCollectionButtonVisible: PropTypes.bool,
  isVendorLogin: PropTypes.bool.isRequired,
  storeSkuData: PropTypes.array,
  currentTab: PropTypes.string,
  errorAddingToCart: PropTypes.bool,
  setShowLoginForm: PropTypes.func.isRequired,
  setErrorAddingToCart: PropTypes.func.isRequired,
  showLoginForm: PropTypes.bool,
  sizeChartData: PropTypes.object,
  fomo: PropTypes.object,
  variantSwatchType: PropTypes.string,
};

export default withProduct(withLabels(withApplicationContext(ProductSpecificationsDesktop)));
